import React from "react";
import api, { API } from "@/lib/api";
import { PageHeader } from "@/components/layout/PageHeader";
import {
  EnvelopeSimple, GoogleLogo, MicrosoftOutlookLogo, ArrowRight,
  CheckCircle, WarningCircle, Clock, ArrowsClockwise, X,
  Receipt, Stack, FileText, MagnifyingGlass, CaretRight,
  ThumbsUp, ThumbsDown, Sparkle, ShieldCheck, SignOut, Prohibit,
  User, CalendarBlank, TextAa, PencilSimple, FloppyDisk,
  Link as LinkIcon, Package, CurrencyDollar, Hash, ArrowUp, ArrowDown,
  Funnel, Coins, Trash, ClockCounterClockwise, ArrowUUpLeft, ArrowUUpRight,
  TrayArrowDown, DotsThree, CircleNotch, Circle,
} from "@phosphor-icons/react";

const money = (n, c) =>
  n == null || n === "" ? "—"
    : `${c ? c + " " : ""}${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const num = (n) => (n == null || n === "" ? "—" : Number(n).toLocaleString());

const fmtDate = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  return isNaN(dt) ? String(d) : dt.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" });
};
const fmtDateTime = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  return isNaN(dt) ? String(d)
    : dt.toLocaleString(undefined, { year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });
};
const fmtClock = (ts) => {
  const dt = new Date(ts);
  return isNaN(dt) ? "" : dt.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", second: "2-digit" });
};

const STATUS_STYLE = {
  PENDING_REVIEW:     { bg: "bg-amber-50",   bd: "border-amber-200",   tx: "text-amber-700",   label: "Pending review" },
  NEEDS_REVIEW:       { bg: "bg-amber-50",   bd: "border-amber-200",   tx: "text-amber-700",   label: "Needs review" },
  RECEIPT_MISMATCH:   { bg: "bg-amber-50",   bd: "border-amber-200",   tx: "text-amber-700",   label: "Receipt mismatch" },
  SUPPLIER_NOT_FOUND: { bg: "bg-amber-50",   bd: "border-amber-200",   tx: "text-amber-700",   label: "Supplier not found" },
  AUTO_APPROVED:      { bg: "bg-emerald-50", bd: "border-emerald-200", tx: "text-emerald-700", label: "Auto-approved" },
  APPROVED:           { bg: "bg-emerald-50", bd: "border-emerald-200", tx: "text-emerald-700", label: "Approved" },
  CREATED:            { bg: "bg-emerald-50", bd: "border-emerald-200", tx: "text-emerald-700", label: "Created in Oracle" },
  POSTED:             { bg: "bg-emerald-50", bd: "border-emerald-200", tx: "text-emerald-700", label: "Posted" },
  REJECTED:           { bg: "bg-red-50",     bd: "border-red-200",     tx: "text-[#DC2626]",   label: "Rejected" },
  FAILED:             { bg: "bg-red-50",     bd: "border-red-200",     tx: "text-[#DC2626]",   label: "Failed" },
  DUPLICATE:          { bg: "bg-red-50",     bd: "border-red-200",     tx: "text-[#DC2626]",   label: "Duplicate" },
};
const styleFor = (s) =>
  STATUS_STYLE[(s || "").toUpperCase()] || { bg: "bg-gray-50", bd: "border-gray-200", tx: "text-gray-600", label: s || "Unknown" };

function StatusBadge({ status }) {
  const s = styleFor(status);
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border ${s.bg} ${s.bd} ${s.tx}`}>
      {s.label}
    </span>
  );
}

function MatchChip({ type, isNonPo }) {
  const t = (type || "").toUpperCase();
  let label = t || "—", cls = "border-gray-200 text-gray-500 bg-gray-50", Icon = FileText;
  if (isNonPo || t === "NON_PO") { label = "Non-PO"; cls = "border-violet-200 text-violet-700 bg-violet-50"; Icon = FileText; }
  else if (t.includes("2")) { label = "2-way"; cls = "border-sky-200 text-sky-700 bg-sky-50"; Icon = LinkIcon; }
  else if (t.includes("3")) { label = "3-way"; cls = "border-indigo-200 text-indigo-700 bg-indigo-50"; Icon = Package; }
  else if (t.includes("4")) { label = "4-way"; cls = "border-indigo-200 text-indigo-700 bg-indigo-50"; Icon = Package; }
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider border ${cls}`}>
      <Icon size={10} weight="bold" /> {label}
    </span>
  );
}

const isNonPo = (r) => {
  if (r?.is_non_po === true) return true;
  if (r?.is_non_po === false) return false;
  const mt = (r?.match_type || "").toUpperCase();
  if (mt === "NON_PO") return true;
  if (mt.includes("2") || mt.includes("3") || mt.includes("4")) return false;
  if (r?.source_table === "non_po_invoice_staging") return true;
  if (r?.source_table === "invoice_staging") return false;
  return !r?.po_number;
};

const rowKey = (r) => `${isNonPo(r) ? "np" : "po"}-${r.id}`;

function ConnectGate({ onConnected }) {
  const [checking, setChecking] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/email-agent/auth/status");
        if (!cancelled && data?.connected) onConnected();
      } catch { /* show connect card */ }
      finally { if (!cancelled) setChecking(false); }
    })();
    return () => { cancelled = true; };
  }, [onConnected]);

  const connectGoogle = () => {
    const token = localStorage.getItem("treasury_token");
    if (!token) { alert("Please sign in again."); return; }
    window.location.href = `${API}/email-agent/auth/google?token=${encodeURIComponent(token)}`;
  };
  const connectOutlook = () => {
    const token = localStorage.getItem("treasury_token");
    if (!token) { alert("Please sign in again."); return; }
    window.location.href = `${API}/email-agent/outlook/auth/microsoft?token=${encodeURIComponent(token)}`;
  };
  if (checking) {
    return (
      <div className="flex items-center justify-center py-24 text-xs tracking-[0.2em] uppercase font-bold text-gray-500">
        <ArrowsClockwise size={14} className="animate-spin mr-2" /> Checking connection…
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-8 py-12">
      <div className="border border-gray-200 bg-white grid md:grid-cols-2 overflow-hidden">
        <div className="p-8 bg-[#4A2C17] text-white flex flex-col justify-center">
          <div className="flex items-center gap-2.5 mb-7">
            <img src="/vaku-logo.png" alt="Vaku" className="h-9 w-9 rounded-lg object-cover bg-white/10" />
            <div className="leading-tight">
              <div className="text-base font-black tracking-tight">Vaku</div>
              <div className="text-[9px] tracking-[0.2em] uppercase text-white/60">the Only AI</div>
            </div>
          </div>
          <div className="w-11 h-11 bg-white/15 flex items-center justify-center mb-5">
            <EnvelopeSimple size={22} weight="bold" />
          </div>
          <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-white/70 mb-2">
            Email Agent · Invoice Automation
          </div>
          <h2 className="text-2xl font-black tracking-tight leading-tight mb-3">
            Connect your inbox.<br />We handle the invoices.
          </h2>
          <p className="text-sm text-white/80 leading-relaxed">
            Connect Gmail once. Every invoice email is read, extracted, matched against
            Oracle, and staged here for your approval — no manual entry.
          </p>
        </div>

        <div className="p-8 flex flex-col justify-center">
          <div className="text-2xl font-black tracking-tight text-gray-900 mb-1">Connect inbox</div>
          <p className="text-xs text-gray-500 mb-6 leading-relaxed">
            Authorize read access so the agent can process incoming invoices.
          </p>
          <button
            onClick={connectGoogle}
            data-testid="email-connect-google"
            className="w-full flex items-center gap-3 border border-gray-300 px-4 py-3 mb-3 hover:border-[#4A2C17] hover:bg-[#4A2C17]/5 transition-colors group"
          >
            <span className="w-8 h-8 bg-white border border-gray-200 flex items-center justify-center shrink-0">
              <GoogleLogo size={18} weight="bold" className="text-[#4285F4]" />
            </span>
            <span className="flex-1 text-left">
              <span className="block text-sm font-bold text-gray-900">Continue with Google</span>
              <span className="block text-[10px] text-gray-500">Gmail inbox access required</span>
            </span>
            <ArrowRight size={15} weight="bold" className="text-[#4A2C17] group-hover:translate-x-0.5 transition-transform" />
          </button>

          <button
            onClick={connectOutlook}
            data-testid="email-connect-outlook"
            className="w-full flex items-center gap-3 border border-gray-300 px-4 py-3 hover:border-[#4A2C17] hover:bg-[#4A2C17]/5 transition-colors group"
          >
            <span className="w-8 h-8 bg-white border border-gray-200 flex items-center justify-center shrink-0">
              <MicrosoftOutlookLogo size={18} weight="bold" className="text-[#0078D4]" />
            </span>
            <span className="flex-1 text-left">
              <span className="block text-sm font-bold text-gray-900">Continue with Outlook</span>
              <span className="block text-[10px] text-gray-500">Microsoft 365 / Exchange</span>
            </span>
            <ArrowRight size={15} weight="bold" className="text-[#4A2C17] group-hover:translate-x-0.5 transition-transform" />
          </button>

          <p className="text-[10px] text-gray-400 leading-relaxed mt-6 text-center">
            Credentials are never stored — OAuth only.
          </p>
        </div>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className="border border-gray-200 bg-white p-5">
      <div className="flex items-center justify-between mb-3">
        <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500">{label}</span>
        <Icon size={16} weight="bold" className={accent || "text-[#0020C2]"} />
      </div>
      <div className="text-3xl font-black tracking-tight text-gray-900">{value}</div>
      {sub && <div className="text-[11px] text-gray-500 mt-1">{sub}</div>}
    </div>
  );
}

const SectionTitle = ({ children, right }) => (
  <div className="flex items-center justify-between mb-2">
    <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0020C2]">{children}</div>
    {right}
  </div>
);
function Section({ title, right, children }) {
  return (
    <div>
      <SectionTitle right={right}>{title}</SectionTitle>
      <div className="grid grid-cols-2 gap-x-6 gap-y-2">{children}</div>
    </div>
  );
}
function KV({ k, v, mono, strong }) {
  return (
    <div className="flex flex-col">
      <span className="text-[10px] uppercase tracking-wider text-gray-400">{k}</span>
      <span className={`text-sm ${mono ? "font-mono" : ""} ${strong ? "font-black text-emerald-700" : "font-medium text-gray-900"}`}>
        {v ?? "—"}
      </span>
    </div>
  );
}
function Flag({ ok, label }) {
  return (
    <div className="flex items-center gap-1.5 text-xs">
      {ok ? <CheckCircle size={14} weight="fill" className="text-emerald-500" />
          : <WarningCircle size={14} weight="fill" className="text-amber-500" />}
      <span className={ok ? "text-gray-700" : "text-amber-700"}>{label}</span>
    </div>
  );
}

function EditField({ icon: Icon, label, value, onChange, mono, placeholder, type = "text" }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] uppercase tracking-wider text-gray-400 flex items-center gap-1">
        {Icon && <Icon size={11} weight="bold" className="text-gray-400" />}{label}
      </span>
      <input
        type={type}
        value={value ?? ""}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className={`border border-gray-300 focus:border-[#0020C2] outline-none px-2.5 py-1.5 text-sm bg-white ${mono ? "font-mono" : ""}`}
      />
    </label>
  );
}

/* -- GL account searchable picker (non-PO edit) -------------------------- */
function GLAccountPicker({ userId, supplierName, value, onChange }) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const debounceRef = React.useRef(null);
  const reqRef = React.useRef(0);

  const fetchGl = React.useCallback((q) => {
    if (!supplierName) return;
    const myReq = ++reqRef.current;
    setLoading(true);
    api.post("/api/internal/automation/invoice/gl-combinations", {
      user_id: userId, supplier_name: supplierName, search: q,
    })
      .then(({ data }) => { if (myReq === reqRef.current) setItems(data?.combinations || []); })
      .catch(() => { if (myReq === reqRef.current) setItems([]); })
      .finally(() => { if (myReq === reqRef.current) setLoading(false); });
  }, [userId, supplierName]);

  React.useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchGl(query.trim()), 300);
    return () => clearTimeout(debounceRef.current);
  }, [query, open, fetchGl]);

  return (
    <div className="flex flex-col gap-1">
      <span className="text-[10px] uppercase tracking-wider text-gray-400 flex items-center gap-1">
        <Coins size={11} weight="bold" className="text-gray-400" /> GL account
      </span>
      <div className="flex items-center gap-1.5">
        <input value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder="101.00.7231…"
          className="flex-1 border border-gray-300 focus:border-[#0020C2] outline-none px-2.5 py-1.5 text-sm bg-white font-mono" />
        <button type="button" onClick={() => { const n = !open; setOpen(n); if (n && items.length === 0) fetchGl(""); }}
          disabled={!supplierName} data-testid="gl-browse"
          className="shrink-0 border border-gray-300 px-2 py-1.5 text-[10px] font-bold uppercase tracking-wider text-gray-700 hover:border-[#0020C2] hover:text-[#0020C2] disabled:opacity-50 inline-flex items-center gap-1">
          Browse
        </button>
      </div>
      {open && (
        <div className="border border-[#0020C2]/25 bg-white">
          <input autoFocus value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder='Search past GL by description — e.g. "condeco"'
            className="w-full px-2.5 py-1.5 text-xs outline-none border-b border-gray-100" />
          <div className="max-h-44 overflow-y-auto">
            {loading && <div className="px-2.5 py-2 text-[11px] text-gray-400 flex items-center gap-2"><ArrowsClockwise size={11} className="animate-spin" /> Loading…</div>}
            {!loading && items.length === 0 && (
              <div className="px-2.5 py-2 text-[11px] text-gray-400">
                {query.trim() ? `No past GL matches "${query.trim()}". Keeping the current GL — you can also type one above.` : "No historical GL combinations for this supplier. Keep the current GL, or type one above."}
              </div>
            )}
            {!loading && items.map((it, i) => (
              <button type="button" key={`${it.gl}-${i}`} onClick={() => { onChange(it.gl); setOpen(false); setQuery(""); }}
                className="w-full text-left px-2.5 py-1.5 hover:bg-[#0020C2]/5 border-b border-gray-50 last:border-0">
                <div className="font-mono text-[11px] text-gray-900 break-all">{it.gl}</div>
                <div className="text-[10px] text-gray-500 flex items-center justify-between gap-2">
                  <span className="truncate">{it.description || "—"}</span>
                  <span className="shrink-0 text-gray-400">{it.use_count > 1 ? `used ${it.use_count}×` : ""}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* -- Business Unit LOV picker (non-PO edit) ------------------------------ */
function BUAccountPicker({ value, onChange }) {
  const [open, setOpen] = React.useState(false);
  const [q, setQ] = React.useState("");
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [offset, setOffset] = React.useState(0);
  const [hasMore, setHasMore] = React.useState(false);
  const listRef = React.useRef(null);
  const debounceRef = React.useRef(null);
  const reqRef = React.useRef(0);
  const PAGE = 25;

  const fetchPage = React.useCallback((search, nextOffset, append) => {
    const myReq = ++reqRef.current;
    setLoading(true);
    api.get("/erp-agents/invoice-create/business-unit-lov", { params: { search, offset: nextOffset, limit: PAGE } })
      .then(({ data }) => {
        if (myReq !== reqRef.current) return;
        const it = data?.items || [];
        setItems((p) => (append ? [...p, ...it] : it));
        setHasMore(!!data?.has_more);
        setOffset(nextOffset + it.length);
      })
      .catch(() => { if (myReq === reqRef.current && !append) setItems([]); })
      .finally(() => { if (myReq === reqRef.current) setLoading(false); });
  }, []);

  React.useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { setOffset(0); fetchPage(q.trim(), 0, false); }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [q, open, fetchPage]);

  const onScroll = (e) => {
    const el = e.target;
    if (!loading && hasMore && el.scrollHeight - el.scrollTop - el.clientHeight < 40) fetchPage(q.trim(), offset, true);
  };

  return (
    <div className="flex flex-col gap-1">
      <span className="text-[10px] uppercase tracking-wider text-gray-400 flex items-center gap-1">
        <Package size={11} weight="bold" className="text-gray-400" /> Business unit
      </span>
      <div className="flex items-center gap-1.5">
        <input value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder="Business unit"
          className="flex-1 border border-gray-300 focus:border-[#0020C2] outline-none px-2.5 py-1.5 text-sm bg-white" />
        <button type="button" onClick={() => { const n = !open; setOpen(n); if (n && items.length === 0) fetchPage("", 0, false); }}
          className="shrink-0 border border-gray-300 px-2 py-1.5 text-[10px] font-bold uppercase tracking-wider text-gray-700 hover:border-[#0020C2] hover:text-[#0020C2]">
          Browse
        </button>
      </div>
      {open && (
        <div className="border border-[#0020C2]/25 bg-white">
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search business units…"
            className="w-full px-2.5 py-1.5 text-xs outline-none border-b border-gray-100" />
          <div ref={listRef} onScroll={onScroll} className="max-h-44 overflow-y-auto">
            {items.map((it, i) => (
              <button type="button" key={`${it.name}-${i}`} onClick={() => { onChange(it.name); setOpen(false); setQ(""); }}
                className="w-full text-left px-2.5 py-1.5 hover:bg-[#0020C2]/5 border-b border-gray-50 last:border-0 flex items-center gap-2">
                <span className="min-w-0 flex-1 truncate text-xs text-gray-800">{it.name}</span>
                {it.number && <span className="text-[10px] text-gray-400 font-mono shrink-0">#{it.number}</span>}
              </button>
            ))}
            {loading && <div className="px-2.5 py-2 text-[11px] text-gray-400">Loading…</div>}
            {!loading && items.length === 0 && <div className="px-2.5 py-2 text-[11px] text-gray-400">No matches.</div>}
            {!loading && hasMore && <div className="px-2.5 py-1.5 text-[10px] text-gray-400 text-center">Scroll for more…</div>}
          </div>
        </div>
      )}
    </div>
  );
}

/* -- Supplier LOV picker (non-PO edit) ----------------------------------- */
function SupplierAccountPicker({ value, onChange }) {
  const [open, setOpen] = React.useState(false);
  const [q, setQ] = React.useState("");
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [offset, setOffset] = React.useState(0);
  const [hasMore, setHasMore] = React.useState(false);
  const listRef = React.useRef(null);
  const debounceRef = React.useRef(null);
  const reqRef = React.useRef(0);
  const PAGE = 25;

  const fetchPage = React.useCallback((search, nextOffset, append) => {
    const myReq = ++reqRef.current;
    setLoading(true);
    api.get("/erp-agents/invoice-create/supplier-lov", { params: { search, offset: nextOffset, limit: PAGE } })
      .then(({ data }) => {
        if (myReq !== reqRef.current) return;
        const it = data?.items || [];
        setItems((p) => (append ? [...p, ...it] : it));
        setHasMore(!!data?.has_more);
        setOffset(nextOffset + it.length);
      })
      .catch(() => { if (myReq === reqRef.current && !append) setItems([]); })
      .finally(() => { if (myReq === reqRef.current) setLoading(false); });
  }, []);

  React.useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { setOffset(0); fetchPage(q.trim(), 0, false); }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [q, open, fetchPage]);

  const onScroll = (e) => {
    const el = e.target;
    if (!loading && hasMore && el.scrollHeight - el.scrollTop - el.clientHeight < 40) fetchPage(q.trim(), offset, true);
  };

  return (
    <div className="flex flex-col gap-1">
      <span className="text-[10px] uppercase tracking-wider text-gray-400 flex items-center gap-1">
        <User size={11} weight="bold" className="text-gray-400" /> Supplier
      </span>
      <div className="flex items-center gap-1.5">
        <input value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder="Supplier name"
          className="flex-1 border border-gray-300 focus:border-[#0020C2] outline-none px-2.5 py-1.5 text-sm bg-white" />
        <button type="button" onClick={() => { const n = !open; setOpen(n); if (n && items.length === 0) fetchPage("", 0, false); }}
          className="shrink-0 border border-gray-300 px-2 py-1.5 text-[10px] font-bold uppercase tracking-wider text-gray-700 hover:border-[#0020C2] hover:text-[#0020C2]">
          Browse
        </button>
      </div>
      {open && (
        <div className="border border-[#0020C2]/25 bg-white">
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search suppliers…"
            className="w-full px-2.5 py-1.5 text-xs outline-none border-b border-gray-100" />
          <div ref={listRef} onScroll={onScroll} className="max-h-44 overflow-y-auto">
            {items.map((it, i) => (
              <button type="button" key={`${it.name}-${i}`} onClick={() => { onChange(it.name); setOpen(false); setQ(""); }}
                className="w-full text-left px-2.5 py-1.5 hover:bg-[#0020C2]/5 border-b border-gray-50 last:border-0 flex items-center gap-2">
                <span className="min-w-0 flex-1 truncate text-xs text-gray-800">{it.name}</span>
                {it.number && <span className="text-[10px] text-gray-400 font-mono shrink-0">#{it.number}</span>}
              </button>
            ))}
            {loading && <div className="px-2.5 py-2 text-[11px] text-gray-400">Loading…</div>}
            {!loading && items.length === 0 && <div className="px-2.5 py-2 text-[11px] text-gray-400">No matches.</div>}
            {!loading && hasMore && <div className="px-2.5 py-1.5 text-[10px] text-gray-400 text-center">Scroll for more…</div>}
          </div>
        </div>
      )}
    </div>
  );
}

function LineItemsTable({ lines, threeWay }) {
  if (!Array.isArray(lines) || lines.length === 0) return null;
  return (
    <div>
      <SectionTitle>Line items · {lines.length}</SectionTitle>
      <div className="border border-gray-200 overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 text-gray-500">
            <tr className="text-left">
              <th className="px-2 py-1.5 font-bold">#</th>
              <th className="px-2 py-1.5 font-bold">Item</th>
              <th className="px-2 py-1.5 font-bold">Description</th>
              <th className="px-2 py-1.5 font-bold text-right">Qty</th>
              {threeWay && <th className="px-2 py-1.5 font-bold text-right">Recv</th>}
              <th className="px-2 py-1.5 font-bold text-right">Price</th>
              <th className="px-2 py-1.5 font-bold text-right">Net</th>
              <th className="px-2 py-1.5 font-bold text-right">Tax</th>
              <th className="px-2 py-1.5 font-bold text-center">Match</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => {
              const net = l.invoice_taxable_amount ?? l.taxable_amount ?? (l.invoice_qty ?? l.quantity) * (l.invoice_price ?? l.price);
              const matched = l.line_matched === true || (l.line_status || "").toUpperCase() === "MATCHED";
              return (
                <tr key={i} className="border-t border-gray-100">
                  <td className="px-2 py-1.5">{l.line_number ?? i + 1}</td>
                  <td className="px-2 py-1.5 font-mono">{l.item_number || "—"}</td>
                  <td className="px-2 py-1.5 max-w-[180px] truncate">{l.description || "—"}</td>
                  <td className="px-2 py-1.5 text-right">{num(l.invoice_qty ?? l.quantity)}</td>
                  {threeWay && <td className="px-2 py-1.5 text-right">{num(l.received_quantity)}</td>}
                  <td className="px-2 py-1.5 text-right">{money(l.invoice_price ?? l.price)}</td>
                  <td className="px-2 py-1.5 text-right">{money(net)}</td>
                  <td className="px-2 py-1.5 text-right">{money(l.tax_amount)}</td>
                  <td className="px-2 py-1.5 text-center">
                    {matched
                      ? <CheckCircle size={14} weight="fill" className="text-emerald-500 inline" />
                      : <WarningCircle size={14} weight="fill" className="text-amber-500 inline" title={l.line_status} />}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* -- Non-PO line items table, with per-line GL account + TAX + inline edit -- */
function NonPoLineItems({ row, onSaveLine }) {
  const lines = Array.isArray(row.invoice_lines) ? row.invoice_lines : [];
  const [editingId, setEditingId] = React.useState(null);
  const [draftGl, setDraftGl] = React.useState("");
  const [draftTax, setDraftTax] = React.useState("");
  const [savingId, setSavingId] = React.useState(null);
  const [taxCodes, setTaxCodes] = React.useState([]);

  React.useEffect(() => {
    if (!row.supplier_name) return;
    api.get("/erp-agents/invoice-create/tax-lov", {
      params: { supplier: row.supplier_name, business_unit: row.business_unit || "" },
    })
      .then(({ data }) => setTaxCodes(data?.items || []))
      .catch(() => setTaxCodes([]));
  }, [row.supplier_name, row.business_unit]);

  if (lines.length === 0) return null;

  const startEdit = (l) => {
    setEditingId(l.id ?? l.line_number);
    setDraftGl(l.recommended_gl_account || "");
    setDraftTax(l.tax_classification || "");
  };
  const cancel = () => { setEditingId(null); setDraftGl(""); setDraftTax(""); };

  const save = async (l) => {
    const lid = l.id;
    if (lid == null) { cancel(); return; }
    setSavingId(lid);
    try {
      const t = taxCodes.find((c) => c.code === draftTax);
      await onSaveLine(row, lid, {
        recommended_gl_account: draftGl,
        tax_classification: draftTax || null,
        tax_rate: t ? t.rate : null,
        tax_grosses_up: t ? !!t.grosses_up : null,
      });
      setEditingId(null); setDraftGl(""); setDraftTax("");
    } finally { setSavingId(null); }
  };

  const total = lines.reduce((s, l) => s + (Number(l.amount) || 0), 0);
  const anyTax = lines.some((l) => l.tax_classification || l.tax_rate || l.tax_amount) || taxCodes.length > 0;

  return (
    <div>
      <SectionTitle right={<span className="text-[10px] text-gray-400 font-mono">{money(total, row.currency)}</span>}>
        Line items · {lines.length}
      </SectionTitle>

      <div className="border border-gray-200 overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 text-gray-500">
            <tr className="text-left">
              <th className="px-2 py-1.5 font-bold">#</th>
              <th className="px-2 py-1.5 font-bold">Description</th>
              <th className="px-2 py-1.5 font-bold text-right">Qty</th>
              <th className="px-2 py-1.5 font-bold text-right">Price</th>
              <th className="px-2 py-1.5 font-bold text-right">Amount</th>
              <th className="px-2 py-1.5 font-bold">GL account</th>
              {anyTax && <th className="px-2 py-1.5 font-bold">Tax</th>}
              <th className="px-2 py-1.5 font-bold text-center">·</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => {
              const key = l.id ?? l.line_number ?? i;
              const isEditing = editingId === (l.id ?? l.line_number);
              const editable = l.id != null;
              return (
                <tr key={key} className="border-t border-gray-100 align-top">
                  <td className="px-2 py-1.5">{l.line_number ?? i + 1}</td>
                  <td className="px-2 py-1.5 max-w-[200px]">{l.description || "—"}</td>
                  <td className="px-2 py-1.5 text-right">{num(l.quantity)}</td>
                  <td className="px-2 py-1.5 text-right">{money(l.unit_price)}</td>
                  <td className="px-2 py-1.5 text-right font-medium">{money(l.amount)}</td>

                  <td className="px-2 py-1.5 min-w-[220px]">
                    {isEditing ? (
                      <GLAccountPicker userId={row.user_id} supplierName={row.supplier_name} value={draftGl} onChange={setDraftGl} />
                    ) : (
                      <div className="min-w-0">
                        <div className="font-mono text-[11px] text-gray-900 break-all">{l.recommended_gl_account || "—"}</div>
                        {l.gl_confidence && <span className="mt-0.5 inline-block"><ConfidenceBadge tier={l.gl_confidence} /></span>}
                      </div>
                    )}
                  </td>

                  {anyTax && (
                    <td className="px-2 py-1.5 min-w-[150px]">
                      {isEditing ? (
                        taxCodes.length > 0 ? (
                          <select value={draftTax} onChange={(e) => setDraftTax(e.target.value)}
                            className="w-full border border-gray-300 focus:border-[#0020C2] outline-none px-1.5 py-1 text-[11px] bg-white">
                            <option value="">— none —</option>
                            {taxCodes.map((t) => <option key={t.code} value={t.code}>{t.code} ({t.rate}%)</option>)}
                          </select>
                        ) : (
                          <input value={draftTax} onChange={(e) => setDraftTax(e.target.value)} placeholder="Tax code"
                            className="w-full border border-gray-300 focus:border-[#0020C2] outline-none px-1.5 py-1 text-[11px]" />
                        )
                      ) : (
                        <div className="text-[11px] text-gray-800">
                          {l.tax_classification || "—"}
                          {l.tax_rate ? <span className="text-gray-400"> ({l.tax_rate}%)</span> : null}
                          {l.tax_amount ? <div className="text-[9px] text-gray-400">tax {money(l.tax_amount)}</div> : null}
                        </div>
                      )}
                    </td>
                  )}

                  <td className="px-2 py-1.5 text-center">
                    {isEditing ? (
                      <div className="flex items-center gap-1.5">
                        <button onClick={() => save(l)} disabled={savingId === l.id}
                          className="inline-flex items-center gap-1 bg-[#0020C2] text-white px-2 py-1 text-[10px] font-bold uppercase tracking-wider hover:bg-[#001AA3] disabled:opacity-50">
                          <FloppyDisk size={11} weight="fill" />{savingId === l.id ? "…" : "Save"}
                        </button>
                        <button onClick={cancel} disabled={savingId === l.id}
                          className="border border-gray-300 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-gray-600 hover:bg-gray-50 disabled:opacity-50">
                          Cancel
                        </button>
                      </div>
                    ) : (
                      editable && (
                        <button onClick={() => startEdit(l)} title="Edit GL & tax for this line" className="text-gray-400 hover:text-[#0020C2]">
                          <PencilSimple size={13} weight="bold" />
                        </button>
                      )
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {lines.some((l) => l.gl_reason) && (
        <div className="mt-1.5 space-y-0.5">
          {lines.filter((l) => l.gl_reason).map((l, i) => (
            <p key={i} className="text-[10px] text-gray-400 leading-snug">
              <span className="font-mono text-gray-500">L{l.line_number ?? i + 1}:</span> {l.gl_reason}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

function ReceiptAllocation({ lines }) {
  const withRcpt = (Array.isArray(lines) ? lines : []).filter((l) => l.receipt_number || l.receipt_line_number);
  if (withRcpt.length === 0) {
    return (
      <div className="border border-dashed border-gray-200 bg-gray-50/60 px-3 py-4 text-center text-[11px] text-gray-400">
        No receipt allocation recorded for this invoice.
      </div>
    );
  }
  return (
    <div className="border border-indigo-200 bg-indigo-50/40 overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="bg-indigo-50 text-indigo-700">
          <tr className="text-left">
            <th className="px-2 py-1.5 font-bold">Line</th>
            <th className="px-2 py-1.5 font-bold">Description</th>
            <th className="px-2 py-1.5 font-bold text-right">Qty</th>
            <th className="px-2 py-1.5 font-bold">Receipt #</th>
            <th className="px-2 py-1.5 font-bold text-right">Rcpt line</th>
          </tr>
        </thead>
        <tbody>
          {withRcpt.map((l, i) => (
            <tr key={i} className="border-t border-indigo-100">
              <td className="px-2 py-1.5">{l.line_number ?? i + 1}</td>
              <td className="px-2 py-1.5 max-w-[180px] truncate">{l.description || "—"}</td>
              <td className="px-2 py-1.5 text-right">{num(l.invoice_qty ?? l.quantity)}</td>
              <td className="px-2 py-1.5 font-mono font-bold text-indigo-800">{l.receipt_number || "—"}</td>
              <td className="px-2 py-1.5 text-right font-mono">{l.receipt_line_number ?? "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ConfidenceBadge({ tier }) {
  const t = (tier || "").toUpperCase();
  let cls = "border-gray-200 text-gray-500 bg-gray-50", label = t || "—";
  if (t === "AUTO")           { cls = "border-emerald-200 text-emerald-700 bg-emerald-50"; label = "Auto"; }
  else if (t === "SUGGEST")   { cls = "border-sky-200 text-sky-700 bg-sky-50";             label = "Suggested"; }
  else if (t === "LAST_USED") { cls = "border-amber-200 text-amber-700 bg-amber-50";       label = "Last used"; }
  else if (t === "USER")      { cls = "border-violet-200 text-violet-700 bg-violet-50";     label = "Manual"; }
  else if (t === "NONE")      { cls = "border-gray-200 text-gray-400 bg-gray-50";          label = "None"; }
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider border ${cls}`}>
      {label}
    </span>
  );
}

function Recommendations({ row }) {
  const site     = row.recommended_site || row.supplier_site || "";
  const siteTier = row.site_confidence || "";
  const siteReason = row.site_reason || "";
  if (!site) return null;
  return (
    <div className="border border-[#0020C2]/25 bg-blue-50/40 p-4" data-testid="recommendations">
      <div className="flex items-center gap-1.5 mb-3">
        <Sparkle size={12} weight="fill" className="text-[#0020C2]" />
        <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0020C2]">Vaku AI recommendations</span>
      </div>
      {site && (
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Package size={13} weight="bold" className="text-gray-500" />
            <span className="text-[10px] uppercase tracking-wider text-gray-400">Supplier site</span>
            <ConfidenceBadge tier={siteTier} />
          </div>
          <div className="text-sm font-bold text-gray-900">{site}</div>
          {siteReason && <p className="text-[11px] text-gray-500 leading-relaxed mt-1">{siteReason}</p>}
        </div>
      )}
    </div>
  );
}

function OracleResult({ row }) {
  let resp = row.oracle_response;
  if (typeof resp === "string") { try { resp = JSON.parse(resp); } catch { resp = null; } }
  const line = resp?.invoiceLines?.[0] || {};
  const invoiceId   = row.oracle_invoice_id   || resp?.InvoiceId || null;
  const invoiceNo   = row.oracle_invoice_number || resp?.InvoiceNumber || row.invoice_number;
  const oracleStat  = row.oracle_status || resp?.ValidationStatus || "Created";
  const matchType   = line.MatchType || row.match_type || "—";
  const receiptNo   = line.ReceiptNumber || null;
  return (
    <div className="border border-emerald-200 bg-emerald-50/60 p-4" data-testid="oracle-result">
      <div className="flex items-center gap-1.5 mb-3">
        <CheckCircle size={13} weight="fill" className="text-emerald-600" />
        <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-emerald-700">Oracle result</span>
      </div>
      <div className="grid grid-cols-2 gap-x-6 gap-y-2">
        <div className="flex flex-col">
          <span className="text-[10px] uppercase tracking-wider text-gray-400">Oracle invoice ID</span>
          <span className="text-sm font-mono font-black text-emerald-800">{invoiceId ?? "—"}</span>
        </div>
        <div className="flex flex-col">
          <span className="text-[10px] uppercase tracking-wider text-gray-400">Invoice number</span>
          <span className="text-sm font-mono font-medium text-gray-900">{invoiceNo ?? "—"}</span>
        </div>
        <div className="flex flex-col">
          <span className="text-[10px] uppercase tracking-wider text-gray-400">Match type</span>
          <span className="text-sm font-medium text-gray-900">{matchType}</span>
        </div>
        <div className="flex flex-col">
          <span className="text-[10px] uppercase tracking-wider text-gray-400">Oracle status</span>
          <span className="text-sm font-medium text-gray-900">{oracleStat ?? "—"}</span>
        </div>
        {receiptNo && (
          <div className="flex flex-col">
            <span className="text-[10px] uppercase tracking-wider text-gray-400">Receipt #</span>
            <span className="text-sm font-mono font-medium text-indigo-800">{receiptNo}</span>
          </div>
        )}
      </div>
      {invoiceId == null && (
        <div className="mt-3 text-[11px] text-amber-700 flex items-center gap-1.5">
          <WarningCircle size={12} weight="fill" />
          Oracle response wasn't saved for this invoice. Re-post or check the backend logs.
        </div>
      )}
    </div>
  );
}

/* -- Detail drawer -- full staging info, edit for non-PO, receipts for 3-way */
function DetailDrawer({ row, onClose, onApprove, onPost, onReject, onDelete, onSaveEdits, onSaveLine, onSendValidation, busy }) {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState({});
  const [saving, setSaving] = React.useState(false);
  const [sites, setSites] = React.useState([]);
  const [sitesLoading, setSitesLoading] = React.useState(false);
  const [sitesErr, setSitesErr] = React.useState("");
  const [siteRec, setSiteRec] = React.useState(null);
  const [recLoading, setRecLoading] = React.useState(false);

  const loadSites = React.useCallback(async (supplierName) => {
    if (!supplierName) { setSites([]); return; }
    setSitesLoading(true); setSitesErr("");
    try {
      const { data } = await api.post("/api/internal/automation/invoice/supplier-sites", {
        user_id: row.user_id, supplier_name: supplierName, procurement_bu: row.business_unit || undefined,
      });
      const list = Array.isArray(data?.sites) ? data.sites : [];
      setSites(list);
      if (list.length === 0) setSitesErr("No active sites found for this supplier.");
    } catch (e) {
      setSites([]);
      setSitesErr(e?.response?.data?.detail || e.message || "Could not fetch sites.");
    } finally { setSitesLoading(false); }
  }, [row?.user_id, row?.business_unit]);

  React.useEffect(() => {
    setEditing(false);
    setSites([]); setSitesErr("");
    setSiteRec(row && row.recommended_site ? {
      recommended_site: row.recommended_site,
      recommendation_level: row.site_confidence || "",
      reason: row.site_reason || "",
      confidence: 0,
    } : null);
    setDraft(row ? {
      supplier_name: row.supplier_name ?? "",
      business_unit: row.business_unit ?? "",
      supplier_site: row.supplier_site ?? "",
      invoice_number: row.invoice_number ?? "",
      invoice_amount: row.invoice_amount ?? "",
      recommended_gl_account: row.recommended_gl_account ?? row.recommended_gl ?? row.charge_account ?? "",
      recommended_site: row.recommended_site ?? row.supplier_site ?? "",
    } : {});

    const alreadyHave = !!(row && row.recommended_site);
    if (!alreadyHave && row && (row.is_non_po === true || (row.match_type || "").toUpperCase() === "NON_PO")) {
      const linesForRec = Array.isArray(row.po_lines) && row.po_lines.length
        ? row.po_lines.map((l) => ({ description: l.description, amount: l.invoice_amount ?? l.amount ?? l.invoice_taxable_amount, quantity: l.invoice_qty ?? l.quantity }))
        : [{ description: row.invoice_number || "", amount: row.invoice_amount, quantity: 1 }];
      setRecLoading(true);
      api.post("/api/internal/automation/invoice/site-recommendation", {
        user_id: row.user_id, supplier_name: row.supplier_name, invoice_lines: linesForRec,
      })
        .then(({ data }) => {
          setSiteRec(data || null);
          if (data?.recommended_site) setDraft((d) => (d.supplier_site ? d : { ...d, supplier_site: data.recommended_site }));
        })
        .catch(() => setSiteRec(null))
        .finally(() => setRecLoading(false));
    }
  }, [row?.id]);

  React.useEffect(() => {
    if (editing && draft.supplier_name && sites.length === 0) loadSites(draft.supplier_name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  if (!row) return null;

  const nonPo = isNonPo(row);
  const st = (row.status || "").toUpperCase();
  const vs = (row.validation_status || "").toUpperCase();
  const aps = (row.approval_status || "").toUpperCase();
  const posted = !!row.oracle_invoice_id || ["POSTED", "CREATED"].includes(st) || ["POSTED", "CREATED"].includes(vs);
  const approvedNotPosted = !posted && (["APPROVED", "AUTO_APPROVED"].includes(aps) || ["APPROVED", "AUTO_APPROVED"].includes(st));
  const FAILED_SET = ["FAILED", "REJECTED", "ORACLE_ERROR", "DUPLICATE"];
  const failedState = !posted && !approvedNotPosted && (FAILED_SET.includes(st) || FAILED_SET.includes(vs));
  const pendingState = !posted && !approvedNotPosted && !failedState;
  const canApprove = pendingState;
  const threeWay = /3|4/.test((row.match_type || "").toUpperCase());
  const lines = Array.isArray(row.po_lines) ? row.po_lines : [];

  const set = (k) => (v) => setDraft((d) => ({ ...d, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await onSaveEdits(row, {
        supplier_name: draft.supplier_name,
        business_unit: draft.business_unit,
        supplier_site: draft.supplier_site,
        invoice_number: draft.invoice_number,
        invoice_amount: draft.invoice_amount === "" ? null : Number(draft.invoice_amount),
        recommended_site: draft.recommended_site,
      });
      setEditing(false);
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50" />
      <div onClick={(e) => e.stopPropagation()} className="relative w-full max-w-2xl bg-white h-full overflow-y-auto shadow-2xl" data-testid="invoice-detail-drawer">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 pt-4 pb-4 z-10">
          <div className="flex items-start justify-between">
            <div className="min-w-0">
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500 mb-1">Invoice · staging #{row.id}</div>
              <h3 className="text-xl font-black tracking-tight">{row.invoice_number || "—"}</h3>
              <div className="mt-1.5 flex items-center gap-2">
                <StatusBadge status={row.validation_status || row.status} />
                <MatchChip type={row.match_type} isNonPo={nonPo} />
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0 ml-3">
              {nonPo && canApprove && !editing && (
                <button onClick={() => setEditing(true)} data-testid="drawer-edit"
                  className="inline-flex items-center gap-1.5 border border-gray-300 px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-wider text-gray-700 hover:border-[#0020C2] hover:text-[#0020C2]">
                  <PencilSimple size={13} weight="bold" /> Edit
                </button>
              )}
              <button onClick={onClose} data-testid="drawer-close"><X size={18} /></button>
            </div>
          </div>

          {(row.email_from || row.email_subject || row.email_date) && (
            <div className="mt-3 border border-gray-200 bg-gray-50/70 px-3 py-2.5" data-testid="drawer-email-meta">
              <div className="flex items-center gap-1.5 mb-2">
                <EnvelopeSimple size={12} weight="bold" className="text-[#4A2C17]" />
                <span className="text-[9px] tracking-[0.2em] uppercase font-bold text-gray-500">From email</span>
              </div>
              <div className="space-y-1.5">
                <div className="flex items-start gap-2"><User size={13} weight="bold" className="text-gray-400 mt-0.5 shrink-0" /><span className="text-xs font-medium text-gray-900 truncate">{row.email_from || "—"}</span></div>
                <div className="flex items-start gap-2"><TextAa size={13} weight="bold" className="text-gray-400 mt-0.5 shrink-0" /><span className="text-xs text-gray-700 leading-snug">{row.email_subject || "—"}</span></div>
                <div className="flex items-start gap-2"><CalendarBlank size={13} weight="bold" className="text-gray-400 mt-0.5 shrink-0" /><span className="text-xs text-gray-500 font-mono">{fmtDateTime(row.email_date)}</span></div>
              </div>
            </div>
          )}
        </div>

        <div className="p-6 space-y-6">
          {editing ? (
            <div className="border border-[#0020C2]/30 bg-blue-50/40 p-4">
              <div className="flex items-center gap-1.5 mb-3">
                <PencilSimple size={12} weight="bold" className="text-[#0020C2]" />
                <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0020C2]">Editing non-PO invoice</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <SupplierAccountPicker value={draft.supplier_name} onChange={set("supplier_name")} />
                <BUAccountPicker value={draft.business_unit} onChange={set("business_unit")} />
                <EditField icon={Hash} label="Invoice number" value={draft.invoice_number} onChange={set("invoice_number")} mono />
                <EditField icon={CurrencyDollar} label={`Amount${row.currency ? " (" + row.currency + ")" : ""}`} value={draft.invoice_amount} onChange={set("invoice_amount")} type="number" mono />
              </div>
              <p className="mt-2 text-[10px] text-gray-400 leading-snug">GL accounts and tax are set per line in the line-items table below — edit each line with its pencil.</p>

              <div className="mt-3 border border-gray-200 bg-white p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] uppercase tracking-wider text-gray-400 flex items-center gap-1"><Package size={11} weight="bold" className="text-gray-400" /> Supplier site</span>
                  <button onClick={() => loadSites(draft.supplier_name)} disabled={sitesLoading || !draft.supplier_name} data-testid="fetch-sites"
                    className="inline-flex items-center gap-1 border border-gray-300 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-gray-700 hover:border-[#0020C2] hover:text-[#0020C2] disabled:opacity-50">
                    <ArrowsClockwise size={11} weight="bold" className={sitesLoading ? "animate-spin" : ""} />{sitesLoading ? "Loading…" : "Reload"}
                  </button>
                </div>

                {sitesLoading ? (
                  <div className="px-2.5 py-2 text-[11px] text-gray-400 flex items-center gap-2 border border-gray-200"><ArrowsClockwise size={11} className="animate-spin" /> Loading sites…</div>
                ) : sites.length > 0 ? (
                  <select value={draft.supplier_site || ""} onChange={(e) => set("supplier_site")(e.target.value)} data-testid="site-select"
                    className="w-full border border-gray-300 focus:border-[#0020C2] outline-none px-2.5 py-1.5 text-sm bg-white font-mono">
                    <option value="" disabled>Select a site…</option>
                    {sites.map((s, i) => (
                      <option key={`${s.site}-${s.procurement_bu}-${i}`} value={s.site}>{s.site}{s.procurement_bu ? ` — ${s.procurement_bu}` : ""}{s.pay_site ? " (pay)" : ""}</option>
                    ))}
                  </select>
                ) : (
                  <input value={draft.supplier_site || ""} onChange={(e) => set("supplier_site")(e.target.value)} placeholder="e.g. ITALY — or click Reload"
                    className="w-full border border-gray-300 focus:border-[#0020C2] outline-none px-2.5 py-1.5 text-sm bg-white font-mono" />
                )}
                {sitesErr && <div className="mt-1.5 text-[11px] text-amber-700">{sitesErr}</div>}
                {sites.length > 0 && <div className="mt-1.5 text-[10px] text-gray-400">{sites.length} active site{sites.length > 1 ? "s" : ""} found</div>}

                {siteRec?.recommended_site && (
                  <div className="mt-2 border border-[#0020C2]/30 bg-blue-50/60 px-2.5 py-2">
                    <div className="flex items-center gap-1.5 mb-1">
                      <Sparkle size={11} weight="fill" className="text-[#0020C2]" />
                      <span className="text-[9px] tracking-[0.15em] uppercase font-bold text-[#0020C2]">Recommended: {siteRec.recommended_site}</span>
                      {siteRec.confidence > 0 && <span className="text-[9px] font-mono text-gray-500">{Math.round(siteRec.confidence)}%</span>}
                    </div>
                    <p className="text-[11px] text-gray-700 leading-snug">{siteRec.reason}</p>
                    {draft.supplier_site !== siteRec.recommended_site && (
                      <button onClick={() => set("supplier_site")(siteRec.recommended_site)} className="mt-1.5 text-[10px] font-bold uppercase tracking-wider text-[#0020C2] hover:underline">Use recommended</button>
                    )}
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2 mt-4">
                <button onClick={save} disabled={saving} data-testid="drawer-save"
                  className="inline-flex items-center gap-1.5 bg-[#0020C2] text-white px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-[#001AA3] disabled:opacity-50">
                  <FloppyDisk size={13} weight="fill" /> {saving ? "Saving…" : "Save changes"}
                </button>
                <button onClick={() => setEditing(false)} disabled={saving}
                  className="border border-gray-300 px-4 py-2 text-xs font-bold uppercase tracking-wider text-gray-600 hover:bg-gray-50 disabled:opacity-50">Cancel</button>
              </div>
            </div>
          ) : (
            <Section title="Invoice">
              <KV k="Supplier" v={row.supplier_name} />
              <KV k="Site" v={row.supplier_site || (nonPo ? siteRec?.recommended_site : null)} />
              <KV k="Amount" v={money(row.invoice_amount, row.currency)} strong />
              <KV k="Invoice date" v={fmtDate(row.invoice_date)} />
              <KV k="Payment terms" v={row.payment_terms} />
              <KV k="Business unit" v={row.business_unit} />
            </Section>
          )}

          {!editing && nonPo && siteRec?.recommended_site && (
            <div className="border border-blue-200 bg-blue-50 p-3" data-testid="site-recommendation">
              <div className="text-[10px] font-bold uppercase tracking-wider text-[#0020C2] mb-1 flex items-center gap-1">
                <Sparkle size={11} weight="fill" /> Recommended site · {siteRec.recommended_site}
                {siteRec.confidence > 0 && <span className="ml-1 font-mono text-gray-500">{Math.round(siteRec.confidence)}%</span>}
                <span className="ml-1 text-[9px] border border-blue-200 bg-white px-1 text-blue-700">{siteRec.recommendation_level}</span>
              </div>
              <p className="text-xs text-gray-800 leading-relaxed">{siteRec.reason}</p>
            </div>
          )}

          {!nonPo && (
            <Section title="Purchase order">
              <KV k="PO number" v={row.po_number} mono />
              <KV k="PO header ID" v={row.po_header_id} mono />
              <KV k="PO total" v={money(row.po_total, row.currency)} />
              <KV k="PO status" v={row.po_status} />
              <KV k="Match type" v={row.match_type} />
              <KV k="Match status" v={row.match_status} />
              <KV k="Matched" v={row.matched ? "Yes" : "No"} />
              <KV k="Variance" v={row.variance} />
            </Section>
          )}

          <Section title="Validation">
            <Flag ok={row.supplier_exists} label="Supplier exists" />
            {!nonPo && <Flag ok={row.po_exists} label="PO exists" />}
            <Flag ok={!row.duplicate_exists} label="Not a duplicate" />
            {threeWay && <Flag ok={row.receipt_exists} label="Receipt found" />}
            {nonPo && <Flag ok={!!(row.recommended_gl_account || row.recommended_gl || row.charge_account)} label="GL account set" />}
          </Section>

          {nonPo && !editing && <Recommendations row={row} />}
          {posted && <OracleResult row={row} />}

          {row.ai_reason && (
            <div className="border border-blue-200 bg-blue-50 p-3">
              <div className="text-[10px] font-bold uppercase tracking-wider text-[#0020C2] mb-1 flex items-center gap-1"><Sparkle size={11} weight="fill" /> Vaku AI reasoning</div>
              <p className="text-xs text-gray-800 leading-relaxed">{row.ai_reason}</p>
            </div>
          )}

          {!nonPo && lines.length > 0 && <LineItemsTable lines={lines} threeWay={threeWay} />}
          {nonPo && <NonPoLineItems row={row} onSaveLine={onSaveLine} />}

          {!editing && failedState && (() => {
            let ev = row.email_val;
            if (typeof ev === "string") { try { ev = JSON.parse(ev); } catch { ev = null; } }
            const has = ev && typeof ev === "object" && (ev.subject || ev.body);
            return has ? <ValidationEmailCard row={row} onSend={onSendValidation} /> : null;
          })()}

          {!nonPo && threeWay && (
            <div><SectionTitle>Receipt allocation</SectionTitle><ReceiptAllocation lines={lines} /></div>
          )}

          {row.ocr_text && (
            <details>
              <summary className="text-[10px] font-bold uppercase tracking-wider text-gray-500 cursor-pointer hover:text-gray-700">OCR text ↓</summary>
              <pre className="text-[10px] font-mono bg-gray-50 border border-gray-200 p-2 mt-1 overflow-x-auto max-h-48 whitespace-pre-wrap">{row.ocr_text}</pre>
            </details>
          )}
        </div>

        {!editing && pendingState && (
          <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 flex gap-3">
            <button onClick={() => onApprove(row)} disabled={busy} data-testid="drawer-approve" className="flex-1 bg-[#0020C2] text-white py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-[#001AA3] disabled:opacity-50 flex items-center justify-center gap-2"><ThumbsUp size={13} weight="fill" /> Approve</button>
            <button onClick={() => onReject(row)} disabled={busy} data-testid="drawer-reject" className="border border-[#DC2626] text-[#DC2626] px-4 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-red-50 disabled:opacity-50 flex items-center gap-2"><ThumbsDown size={13} weight="fill" /> Reject</button>
            <button onClick={() => onDelete(row)} disabled={busy} data-testid="drawer-delete" title="Hide this invoice from the list. You can undo from the activity log." className="border border-gray-300 text-gray-600 px-4 py-2.5 text-xs font-bold uppercase tracking-wider hover:border-[#4A2C17] hover:text-[#4A2C17] hover:bg-[#4A2C17]/5 disabled:opacity-50 flex items-center gap-2"><Trash size={13} weight="bold" /> Delete</button>
          </div>
        )}

        {!editing && failedState && (
          <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 flex gap-3">
            <button onClick={() => onReject(row)} disabled={busy} data-testid="drawer-reject" className="flex-1 border border-[#DC2626] text-[#DC2626] py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-red-50 disabled:opacity-50 flex items-center justify-center gap-2"><ThumbsDown size={13} weight="fill" /> Reject</button>
            <button onClick={() => onDelete(row)} disabled={busy} data-testid="drawer-delete" title="Hide this invoice from the list. You can undo from the activity log." className="border border-gray-300 text-gray-600 px-4 py-2.5 text-xs font-bold uppercase tracking-wider hover:border-[#4A2C17] hover:text-[#4A2C17] hover:bg-[#4A2C17]/5 disabled:opacity-50 flex items-center gap-2"><Trash size={13} weight="bold" /> Delete</button>
          </div>
        )}

        {!editing && approvedNotPosted && (
          <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 flex gap-3">
            <button onClick={() => onPost(row)} disabled={busy} data-testid="drawer-post" className="flex-1 bg-emerald-600 text-white py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-emerald-700 disabled:opacity-50 flex items-center justify-center gap-2"><ArrowRight size={13} weight="bold" /> {busy ? "Posting…" : "Post to Oracle"}</button>
            <button onClick={() => onReject(row)} disabled={busy} data-testid="drawer-reject" className="border border-[#DC2626] text-[#DC2626] px-4 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-red-50 disabled:opacity-50 flex items-center gap-2"><ThumbsDown size={13} weight="fill" /> Reject</button>
          </div>
        )}

        {!editing && posted && (
          <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4">
            <div className="flex items-center justify-center gap-2 text-xs font-bold uppercase tracking-wider text-emerald-700"><CheckCircle size={14} weight="fill" /> Created in Oracle</div>
          </div>
        )}
      </div>
    </div>
  );
}

function InvoiceRow({ row, onOpen }) {
  const nonPo = isNonPo(row);
  return (
    <button onClick={() => onOpen(row)} data-testid={`invoice-row-${row.id}`}
      className="w-full grid grid-cols-[1fr_auto] items-center gap-4 border border-gray-200 bg-white px-4 py-3 hover:border-[#0020C2] hover:bg-blue-50/30 transition-colors text-left">
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-bold text-sm text-gray-900 truncate">{row.supplier_name || "Unknown supplier"}</span>
          <StatusBadge status={row.validation_status || row.status} />
          <MatchChip type={row.match_type} isNonPo={nonPo} />
        </div>
        <div className="text-[11px] text-gray-500 mt-0.5 flex items-center gap-2 flex-wrap">
          <span className="font-mono">{row.invoice_number || "—"}</span>
          {row.po_number && <><span className="text-gray-300">·</span><span className="font-mono">PO {row.po_number}</span></>}
          <span className="text-gray-300">·</span>
          <span>{fmtDate(row.invoice_date)}</span>
        </div>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <span className="font-black text-sm text-gray-900">{money(row.invoice_amount, row.currency)}</span>
        <CaretRight size={14} weight="bold" className="text-gray-300" />
      </div>
    </button>
  );
}

function DeletedRow({ row, onRestore, onOpen }) {
  const nonPo = isNonPo(row);
  return (
    <div className="w-full grid grid-cols-[1fr_auto] items-center gap-4 border border-gray-200 bg-gray-50/60 px-4 py-3">
      <button onClick={() => onOpen(row)} className="min-w-0 text-left" data-testid={`deleted-row-${row.id}`}>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-bold text-sm text-gray-700 truncate line-through decoration-gray-300">{row.supplier_name || "Unknown supplier"}</span>
          <StatusBadge status={row.validation_status || row.status} />
          <MatchChip type={row.match_type} isNonPo={nonPo} />
        </div>
        <div className="text-[11px] text-gray-500 mt-0.5 flex items-center gap-2 flex-wrap">
          <span className="font-mono">{row.invoice_number || "—"}</span>
          {row.po_number && <><span className="text-gray-300">·</span><span className="font-mono">PO {row.po_number}</span></>}
          <span className="text-gray-300">·</span>
          <span>deleted {fmtDateTime(row.deleted_at)}</span>
        </div>
      </button>
      <div className="flex items-center gap-3 shrink-0">
        <span className="font-black text-sm text-gray-500">{money(row.invoice_amount, row.currency)}</span>
        <button onClick={() => onRestore(row)} data-testid={`restore-${row.id}`} title="Restore this invoice to the active lists."
          className="inline-flex items-center gap-1.5 border border-emerald-300 text-emerald-700 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider hover:bg-emerald-50">
          <ArrowUUpLeft size={12} weight="bold" /> Restore
        </button>
      </div>
    </div>
  );
}

function Group({ icon: Icon, title, count, accent, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <Icon size={15} weight="fill" className={accent ? "text-[#0020C2]" : "text-gray-400"} />
        <h2 className="text-[11px] tracking-[0.2em] uppercase font-bold text-gray-700">{title}</h2>
        <span className="text-[10px] font-bold text-gray-400">· {count}</span>
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}
const Empty = ({ children }) => (
  <div className="border border-dashed border-gray-200 bg-gray-50/50 px-4 py-6 text-center text-xs text-gray-400">{children}</div>
);

function Segmented({ options, value, onChange }) {
  return (
    <div className="inline-flex border border-gray-300 bg-white">
      {options.map((o) => (
        <button key={o.value} onClick={() => onChange(o.value)}
          className={`px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider transition-colors ${value === o.value ? "bg-[#0020C2] text-white" : "text-gray-500 hover:text-gray-800"} ${o.value !== options[options.length - 1].value ? "border-r border-gray-300" : ""}`}>
          {o.label}{o.count != null ? ` · ${o.count}` : ""}
        </button>
      ))}
    </div>
  );
}

function LogIcon({ kind }) {
  if (kind === "arrived")  return <TrayArrowDown size={13} weight="bold" className="text-[#0020C2]" />;
  if (kind === "deleted")  return <Trash size={13} weight="bold" className="text-[#4A2C17]" />;
  if (kind === "restored") return <ArrowUUpLeft size={13} weight="bold" className="text-emerald-600" />;
  return <DotsThree size={13} weight="bold" className="text-gray-400" />;
}

function ActivityLog({ open, onClose, entries, canUndo, canRedo, onUndo, onRedo, onClear }) {
  if (!open) return null;
  return (
    <>
      <div className="fixed inset-0 z-[60]" onClick={onClose} />
      <div className="absolute right-0 mt-2 w-[360px] max-h-[70vh] bg-white border border-gray-200 shadow-2xl z-[61] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="activity-log">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <div className="flex items-center gap-1.5"><ClockCounterClockwise size={14} weight="bold" className="text-[#4A2C17]" /><span className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-700">Activity log</span></div>
          <button onClick={onClose} data-testid="log-close"><X size={15} className="text-gray-400 hover:text-gray-700" /></button>
        </div>
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-gray-100 bg-gray-50/60">
          <button onClick={onUndo} disabled={!canUndo} data-testid="log-undo" className="inline-flex items-center gap-1.5 border border-gray-300 px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wider text-gray-700 hover:border-[#0020C2] hover:text-[#0020C2] disabled:opacity-40 disabled:hover:border-gray-300 disabled:hover:text-gray-700"><ArrowUUpLeft size={12} weight="bold" /> Undo</button>
          <button onClick={onRedo} disabled={!canRedo} data-testid="log-redo" className="inline-flex items-center gap-1.5 border border-gray-300 px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wider text-gray-700 hover:border-[#0020C2] hover:text-[#0020C2] disabled:opacity-40 disabled:hover:border-gray-300 disabled:hover:text-gray-700"><ArrowUUpRight size={12} weight="bold" /> Redo</button>
          <div className="flex-1" />
          {entries.length > 0 && <button onClick={onClear} data-testid="log-clear" className="text-[10px] font-bold uppercase tracking-wider text-gray-400 hover:text-[#DC2626]">Clear</button>}
        </div>
        <div className="overflow-y-auto flex-1">
          {entries.length === 0 ? (
            <div className="px-4 py-8 text-center text-xs text-gray-400">Nothing yet. Arrivals and deletes will show up here.</div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {entries.map((e) => (
                <li key={e.key} className="px-4 py-2.5 flex items-start gap-2.5">
                  <div className="mt-0.5 shrink-0"><LogIcon kind={e.kind} /></div>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs text-gray-800 leading-snug">
                      {e.kind === "arrived"  && <>New invoice <span className="font-semibold">{e.label}</span> arrived</>}
                      {e.kind === "deleted"  && <>Deleted <span className="font-semibold">{e.label}</span></>}
                      {e.kind === "restored" && <>Restored <span className="font-semibold">{e.label}</span></>}
                    </div>
                    {e.sub && <div className="text-[10px] text-gray-400 truncate">{e.sub}</div>}
                  </div>
                  <div className="text-[10px] font-mono text-gray-400 shrink-0">{fmtClock(e.ts)}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </>
  );
}

const JOB_PIPELINE = [
  { key: "received",   label: "Received mail",        match: ["received mail", "email received"] },
  { key: "parsing",    label: "Parsing document",     match: ["parsing"] },
  { key: "extracting", label: "Extracting fields",    match: ["extract"] },
  { key: "supplier",   label: "Checking supplier",    match: ["supplier"] },
  { key: "po",         label: "Checking PO details",  match: ["po detail", "po details"] },
  { key: "duplicate",  label: "Checking duplicates",  match: ["duplicate"] },
  { key: "match",      label: "Match validation",     match: ["match"] },
  { key: "recommend",  label: "GL & site",            match: ["gl", "site", "recommend"] },
  { key: "staging",    label: "Saving to staging",    match: ["staging", "saving"] },
  { key: "attachment", label: "Uploading attachment", match: ["attachment", "upload"] },
  { key: "completed",  label: "Completed",            match: ["completed", "done", "skipped"] },
];

function jobIsTerminal(step, status) {
  const s = (step || "").toLowerCase();
  const st = (status || "").toUpperCase();
  return st === "COMPLETED" || st === "FAILED" || st === "ERROR" || ["completed", "done", "skipped", "failed"].some((t) => s.includes(t));
}

function useSheenStyle() {
  React.useEffect(() => {
    if (document.getElementById("vaku-sheen-style")) return;
    const el = document.createElement("style");
    el.id = "vaku-sheen-style";
    el.textContent = `
      @keyframes vakuSheen { 0% { background-position: -200px 0; } 100% { background-position: 300px 0; } }
      @keyframes vakuFadeOut { to { opacity: 0; transform: scale(0.97); } }
      .vaku-sheen { position:absolute; inset:0; background:linear-gradient(90deg, transparent, rgba(255,255,255,0.55), transparent); background-size:200px 100%; animation: vakuSheen 1.3s linear infinite; }
      .vaku-fade-out { animation: vakuFadeOut 0.45s ease forwards; }
    `;
    document.head.appendChild(el);
  }, []);
}

const MAIL_CARD_HARD_TIMEOUT_MS = 3 * 60 * 1000;

function MailCard({ job, onDone }) {
  useSheenStyle();
  const [step, setStep] = React.useState(job.step || "");
  const [progress, setProgress] = React.useState(Number(job.progress) || 0);
  const [status, setStatus] = React.useState(job.status || "PROCESSING");
  const [leaving, setLeaving] = React.useState(false);
  const doneRef = React.useRef(false);

  const finish = React.useCallback((finalStatus) => {
    if (doneRef.current) return;
    doneRef.current = true;
    setStatus(finalStatus);
    if (finalStatus === "COMPLETED") setProgress(100);
    setTimeout(() => setLeaving(true), 1800);
    setTimeout(() => onDone?.(job.job_id, finalStatus), 2250);
  }, [job.job_id, onDone]);

  React.useEffect(() => {
    const jobId = job.job_id;
    if (!jobId) return;
    const es = new EventSource(`${API}/api/jobs/${encodeURIComponent(jobId)}/events`);
    es.onmessage = (ev) => {
      let d; try { d = JSON.parse(ev.data); } catch { return; }
      if (d.step) setStep(d.step);
      if (d.progress !== "" && d.progress != null) { const p = Number(d.progress); if (!Number.isNaN(p)) setProgress(p); }
      if (d.status) setStatus(d.status);
      if (jobIsTerminal(d.step, d.status) && !doneRef.current) {
        const final = (d.status || "").toUpperCase() === "FAILED" || (d.step || "").toLowerCase().includes("failed") ? "FAILED" : "COMPLETED";
        es.close(); finish(final);
      }
    };
    es.onerror = () => { };
    const hardTimeout = setTimeout(() => { es.close(); finish("FAILED"); }, MAIL_CARD_HARD_TIMEOUT_MS);
    return () => { es.close(); clearTimeout(hardTimeout); };
  }, [job.job_id, finish]);

  const failed = (status || "").toUpperCase() === "FAILED" || (step || "").toLowerCase().includes("failed");
  const done = !failed && ((status || "").toUpperCase() === "COMPLETED" || (step || "").toLowerCase().includes("completed") || (step || "").toLowerCase().includes("skipped"));
  const pct = Math.min(100, Math.max(0, Math.round(progress)));
  const barColor = failed ? "#DC2626" : done ? "#10B981" : "#0020C2";
  const pctColor = failed ? "text-[#DC2626]" : done ? "text-emerald-600" : "text-[#0020C2]";
  const barWidth = (failed || done) ? 100 : pct;

  return (
    <div className={`bg-white border border-gray-200 overflow-hidden p-4 ${leaving ? "vaku-fade-out" : ""}`} style={{ borderRadius: 12 }} data-testid={`mail-card-${job.job_id}`}>
      <div className="flex items-start gap-2.5">
        <div className="w-8 h-8 bg-[#4A2C17] text-white flex items-center justify-center shrink-0"><EnvelopeSimple size={16} weight="bold" /></div>
        <div className="min-w-0 flex-1">
          <div className="text-[9px] tracking-[0.2em] uppercase font-bold text-gray-400">Received from</div>
          <div className="text-sm font-bold text-gray-900 truncate">{job.sender || "Unknown sender"}</div>
          {job.subject && <div className="text-[11px] text-gray-500 truncate">{job.subject}</div>}
        </div>
        <div className={`text-lg font-black tabular-nums shrink-0 ${pctColor}`}>{pct}%</div>
      </div>
      <div className={`text-[11px] mt-2 mb-2.5 truncate ${failed ? "text-[#DC2626]" : "text-gray-500"}`}>{step || "Starting…"}</div>
      <div className="h-1.5 w-full overflow-hidden relative" style={{ borderRadius: 3, background: "#F3F4F6" }}>
        <div className="h-full relative overflow-hidden transition-all duration-500" style={{ width: `${barWidth}%`, borderRadius: 3, background: barColor }}>
          {!done && !failed && <div className="vaku-sheen" />}
        </div>
      </div>
    </div>
  );
}

const _dismissedJobs = new Set();

function ActiveJobs({ onJobComplete }) {
  const [jobs, setJobs] = React.useState([]);
  const timerRef = React.useRef(null);
  const poll = React.useCallback(async () => {
    try {
      const { data } = await api.get(`/api/jobs/active`);
      const list = Array.isArray(data?.jobs) ? data.jobs : [];
      setJobs(list.filter((j) => !_dismissedJobs.has(j.job_id) && !jobIsTerminal(j.step, j.status)));
    } catch { }
  }, []);
  React.useEffect(() => { poll(); timerRef.current = setInterval(poll, 4000); return () => clearInterval(timerRef.current); }, [poll]);
  const handleDone = React.useCallback((jobId, final) => { _dismissedJobs.add(jobId); setJobs((prev) => prev.filter((j) => j.job_id !== jobId)); onJobComplete?.(jobId, final); }, [onJobComplete]);
  if (!jobs.length) return null;
  return (
    <div className="space-y-3" data-testid="active-jobs">
      <div className="flex items-center gap-2"><CircleNotch size={14} weight="bold" className="text-[#0020C2] animate-spin" /><h2 className="text-[11px] tracking-[0.2em] uppercase font-bold text-gray-700">Incoming mail · being processed</h2><span className="text-[10px] font-bold text-gray-400">· {jobs.length}</span></div>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">{jobs.map((j) => <MailCard key={j.job_id} job={j} onDone={handleDone} />)}</div>
    </div>
  );
}

function ValidationEmailCard({ row, onSend }) {
  let ev = row.email_val;
  if (typeof ev === "string") { try { ev = JSON.parse(ev); } catch { ev = {}; } }
  if (!ev || typeof ev !== "object") ev = {};
  const [to, setTo] = React.useState(ev.to_email || row.email_from || "");
  const [subject, setSubject] = React.useState(ev.subject || `Regarding invoice ${row.invoice_number || ""}`);
  const [body, setBody] = React.useState(ev.body || "");
  const [sending, setSending] = React.useState(false);
  const [sent, setSent] = React.useState(!!row.validation_email_sent_at);
  const [copied, setCopied] = React.useState(false);
  const nonPo = isNonPo(row);

  const send = async () => {
    if (!to.trim() || !body.trim()) { alert("A recipient and message are required to send."); return; }
    setSending(true);
    try { await onSend(row, { to_email: to.trim(), subject, body }); setSent(true); } finally { setSending(false); }
  };
  const copy = async () => { try { await navigator.clipboard.writeText(body); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { } };
  const mailto = `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

  return (
    <div className="border border-amber-200 bg-white" data-testid={`action-card-${row.id}`}>
      <div className="flex items-start justify-between gap-3 px-4 py-3 border-b border-amber-100 bg-amber-50/50">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap"><span className="font-bold text-sm text-gray-900 truncate">{row.supplier_name || "Unknown supplier"}</span><StatusBadge status={row.validation_status || row.status} /><MatchChip type={row.match_type} isNonPo={nonPo} /></div>
          <div className="text-[11px] text-gray-500 mt-0.5 flex items-center gap-2 flex-wrap"><span className="font-mono">{row.invoice_number || "—"}</span><span className="text-gray-300">·</span><span>{money(row.invoice_amount, row.currency)}</span><span className="text-gray-300">·</span><span>{fmtDate(row.invoice_date)}</span></div>
        </div>
        {sent && <span className="shrink-0 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-700 border border-emerald-200 bg-emerald-50 px-2 py-0.5"><CheckCircle size={11} weight="fill" /> Sent</span>}
      </div>
      {row.ai_reason && (
        <div className="px-4 pt-3"><div className="text-[10px] font-bold uppercase tracking-wider text-amber-700 mb-1 flex items-center gap-1"><WarningCircle size={11} weight="fill" /> Why it failed</div><p className="text-xs text-gray-700 leading-relaxed">{row.ai_reason}</p></div>
      )}
      <div className="p-4 space-y-2.5">
        <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0020C2] flex items-center gap-1"><Sparkle size={11} weight="fill" /> Drafted email to supplier</div>
        {!ev.subject && !ev.body ? (
          <div className="border border-dashed border-gray-200 bg-gray-50/60 px-3 py-4 text-center text-[11px] text-gray-400">No drafted email yet for this invoice. It's generated when validation fails.</div>
        ) : (
          <>
            <label className="flex flex-col gap-1"><span className="text-[10px] uppercase tracking-wider text-gray-400">To</span><input value={to} onChange={(e) => setTo(e.target.value)} placeholder="supplier@example.com" className="border border-gray-300 focus:border-[#0020C2] outline-none px-2.5 py-1.5 text-sm bg-white font-mono" /></label>
            <label className="flex flex-col gap-1"><span className="text-[10px] uppercase tracking-wider text-gray-400">Subject</span><input value={subject} onChange={(e) => setSubject(e.target.value)} className="border border-gray-300 focus:border-[#0020C2] outline-none px-2.5 py-1.5 text-sm bg-white" /></label>
            <label className="flex flex-col gap-1"><span className="text-[10px] uppercase tracking-wider text-gray-400">Message</span><textarea value={body} onChange={(e) => setBody(e.target.value)} rows={7} className="border border-gray-300 focus:border-[#0020C2] outline-none px-2.5 py-1.5 text-sm bg-white leading-relaxed resize-y" /></label>
            <div className="flex items-center gap-2 pt-1">
              <button onClick={send} disabled={sending} data-testid={`send-validation-${row.id}`} className="inline-flex items-center gap-1.5 bg-[#0020C2] text-white px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-[#001AA3] disabled:opacity-50"><ArrowRight size={13} weight="bold" /> {sending ? "Sending…" : sent ? "Send again" : "Send"}</button>
              <button onClick={copy} className="inline-flex items-center gap-1.5 border border-gray-300 px-3 py-2 text-xs font-bold uppercase tracking-wider text-gray-700 hover:border-[#0020C2] hover:text-[#0020C2]">{copied ? "Copied" : "Copy"}</button>
              <a href={mailto} className="inline-flex items-center gap-1.5 border border-gray-300 px-3 py-2 text-xs font-bold uppercase tracking-wider text-gray-700 hover:border-[#4A2C17] hover:text-[#4A2C17]"><EnvelopeSimple size={13} weight="bold" /> Open in Mail</a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* -- Dashboard ------------------------------------------------------------ */
function Dashboard({ onDisconnect }) {
  const [rows, setRows]         = React.useState([]);
  const [loading, setLoading]   = React.useState(true);
  const [err, setErr]           = React.useState("");
  const [q, setQ]               = React.useState("");
  const [tab, setTab]           = React.useState("invoices");
  const [typeFilter, setType]   = React.useState("all");
  const [sortKey, setSortKey]   = React.useState("date");
  const [sortDir, setSortDir]   = React.useState("desc");
  const [selected, setSelected] = React.useState(null);
  const [actionBusy, setBusy]   = React.useState(false);
  const [connEmail, setConnEmail] = React.useState("");

  const [deletedIds, setDeletedIds] = React.useState(() => new Set());
  const [log, setLog]               = React.useState([]);
  const [undoStack, setUndoStack]   = React.useState([]);
  const [redoStack, setRedoStack]   = React.useState([]);
  const [logOpen, setLogOpen]       = React.useState(false);
  const [toast, setToast]           = React.useState(null);
  const [deletedRows, setDeletedRows] = React.useState([]);
  const [deletedLoading, setDeletedLoading] = React.useState(false);
  const toastTimerRef = React.useRef(null);
  const seenIdsRef = React.useRef(null);

  const showToast = React.useCallback((msg) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ msg });
    toastTimerRef.current = setTimeout(() => setToast(null), 2000);
  }, []);
  React.useEffect(() => () => { if (toastTimerRef.current) clearTimeout(toastTimerRef.current); }, []);

  const keyRef = React.useRef(0);
  const nextKey = () => `log-${Date.now()}-${keyRef.current++}`;
  const pushLog = React.useCallback((kind, row) => {
    setLog((prev) => [{ key: nextKey(), kind, ts: Date.now(), id: row.id, label: row.invoice_number || `#${row.id}`, sub: row.supplier_name || "" }, ...prev].slice(0, 100));
  }, []);

  const load = React.useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const [{ data: inv }, statusRes] = await Promise.all([
        api.get("/email-agent/invoices"),
        api.get("/email-agent/auth/status").catch(() => ({ data: {} })),
      ]);
      const list = Array.isArray(inv) ? inv : inv?.invoices || [];
      setRows(list);
      setConnEmail(statusRes?.data?.email || "");
      const keys = list.map((r) => rowKey(r));
      if (seenIdsRef.current === null) { seenIdsRef.current = new Set(keys); }
      else {
        const fresh = list.filter((r) => !seenIdsRef.current.has(rowKey(r)));
        fresh.forEach((r) => { seenIdsRef.current.add(rowKey(r)); pushLog("arrived", r); });
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Could not load invoices.");
      setRows([]);
    } finally { setLoading(false); }
  }, [pushLog]);

  React.useEffect(() => { load(); }, [load]);

  const loadDeleted = React.useCallback(async () => {
    setDeletedLoading(true);
    try { const { data } = await api.get("/email-agent/invoices/deleted"); setDeletedRows(Array.isArray(data) ? data : data?.invoices || []); }
    catch { setDeletedRows([]); } finally { setDeletedLoading(false); }
  }, []);
  React.useEffect(() => { if (tab === "deleted") loadDeleted(); }, [tab, loadDeleted]);

  const disconnect = async () => {
    if (!window.confirm("Disconnect this Gmail mailbox? The agent will stop processing its emails.")) return;
    try { await api.post("/email-agent/auth/disconnect"); onDisconnect?.(); } catch (e) { alert(e?.response?.data?.detail || e.message); }
  };

  const norm = (s) => (s || "").toUpperCase();
  const POSTED = ["POSTED", "CREATED"];
  const FAILED = ["FAILED", "REJECTED", "ORACLE_ERROR", "DUPLICATE"];
  const APPROVED = ["APPROVED", "AUTO_APPROVED"];
  const isCreated = (r) => !!r.oracle_invoice_id || POSTED.includes(norm(r.status)) || POSTED.includes(norm(r.validation_status)) || norm(r.fusion_status) === "CREATED";
  const isFailed = (r) => { if (isCreated(r)) return false; return FAILED.includes(norm(r.status)) || FAILED.includes(norm(r.validation_status)); };
  const isApproved = (r) => { if (isCreated(r) || isFailed(r)) return false; return APPROVED.includes(norm(r.approval_status)) || APPROVED.includes(norm(r.status)); };
  const isPending = (r) => !isCreated(r) && !isFailed(r) && !isApproved(r);

  const matchText = (r) => { if (!q.trim()) return true; const t = q.toLowerCase(); return [r.supplier_name, r.invoice_number, r.po_number].some((x) => (x || "").toLowerCase().includes(t)); };
  const matchTypeFilter = (r) => {
    if (typeFilter === "all") return true;
    const mt = norm(r.match_type);
    if (typeFilter === "nonpo") return isNonPo(r);
    if (typeFilter === "2way")  return !isNonPo(r) && mt.includes("2");
    if (typeFilter === "3way")  return !isNonPo(r) && (mt.includes("3") || mt.includes("4"));
    return true;
  };
  const sortRows = (list) => {
    const dir = sortDir === "asc" ? 1 : -1;
    return [...list].sort((a, b) => {
      let av, bv;
      if (sortKey === "amount")      { av = Number(a.invoice_amount) || 0; bv = Number(b.invoice_amount) || 0; }
      else if (sortKey === "supplier") { av = (a.supplier_name || "").toLowerCase(); bv = (b.supplier_name || "").toLowerCase(); }
      else                           { av = new Date(a.invoice_date || a.created_at || 0).getTime(); bv = new Date(b.invoice_date || b.created_at || 0).getTime(); }
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  };

  const notDeleted = (r) => !deletedIds.has(rowKey(r));
  const visible = (r) => notDeleted(r) && matchText(r) && matchTypeFilter(r);
  const pending  = sortRows(rows.filter((r) => isPending(r)  && visible(r)));
  const approved = sortRows(rows.filter((r) => isApproved(r) && visible(r)));
  const created  = sortRows(rows.filter((r) => isCreated(r)  && visible(r)));
  const failed   = sortRows(rows.filter((r) => isFailed(r)   && visible(r)));
  const pendingValue = pending.reduce((s, r) => s + (Number(r.invoice_amount) || 0), 0);
  const currency = rows.find((r) => r.currency)?.currency || "";

  const approve = async (row) => {
    setBusy(true);
    try {
      try { await api.post(`/email-agent/invoices/${row.id}/approve`); }
      catch { try { await api.patch(`/email-agent/invoices/${row.id}`, { approval_status: "APPROVED" }); } catch {} }
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, approval_status: "APPROVED" } : r)));
      setSelected(null);
    } catch (e) { alert(e?.response?.data?.detail || e.message); } finally { setBusy(false); }
  };

  const postToOracle = async (row) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/api/internal/invoice/create`, { staging_id: row.id, user_id: row.user_id, is_non_po: isNonPo(row) });
      const patch = {
        oracle_invoice_id: data?.oracle_invoice_id ?? row.oracle_invoice_id,
        oracle_invoice_number: data?.oracle_invoice_number ?? row.oracle_invoice_number,
        oracle_status: data?.oracle_status ?? "CREATED",
        status: "CREATED",
        oracle_response: data?.oracle_response ?? data ?? row.oracle_response,
      };
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, ...patch } : r)));
      setSelected((s) => (s && s.id === row.id ? { ...s, ...patch } : s));
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "object" ? JSON.stringify(detail) : (detail || e.message);
      alert("Oracle rejected the invoice:\n\n" + msg);
    } finally { setBusy(false); }
  };

  const reject = async (row) => {
    if (!window.confirm("Reject this invoice?")) return;
    setBusy(true);
    try {
      try { await api.post(`/email-agent/invoices/${row.id}/reject`); }
      catch { try { await api.patch(`/email-agent/invoices/${row.id}`, { approval_status: "REJECTED", status: "REJECTED" }); } catch {} }
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, status: "REJECTED", approval_status: "REJECTED" } : r)));
      setSelected(null);
    } catch (e) { alert(e?.response?.data?.detail || e.message); } finally { setBusy(false); }
  };

  const deleteInvoice = async (row) => {
    if (!isPending(row) && !isFailed(row)) { alert("Only invoices that are for approval or need review can be deleted."); return; }
    const src = isNonPo(row) ? "non_po" : "po";
    setDeletedIds((prev) => { const n = new Set(prev); n.add(rowKey(row)); return n; });
    setUndoStack((prev) => [...prev, { row }]); setRedoStack([]);
    pushLog("deleted", row);
    showToast(`Deleted ${row.invoice_number || `#${row.id}`} — undo from the log`);
    setSelected(null);
    try {
      await api.post(`/email-agent/invoices/${row.id}/delete?source=${src}`);
      setRows((prev) => prev.filter((r) => rowKey(r) !== rowKey(row)));
      setDeletedRows((prev) => (prev.some((r) => rowKey(r) === rowKey(row)) ? prev : [row, ...prev]));
    } catch (e) {
      setDeletedIds((prev) => { const n = new Set(prev); n.delete(rowKey(row)); return n; });
      setUndoStack((prev) => prev.filter((u) => rowKey(u.row) !== rowKey(row)));
      alert(e?.response?.data?.detail || e.message || "Could not delete invoice.");
    }
  };

  const undo = () => {
    setUndoStack((prev) => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      const src = isNonPo(last.row) ? "non_po" : "po";
      setDeletedIds((ids) => { const next = new Set(ids); next.delete(rowKey(last.row)); return next; });
      setRedoStack((r) => [...r, last]);
      pushLog("restored", last.row);
      api.post(`/email-agent/invoices/${last.row.id}/restore?source=${src}`).then(() => load()).catch((e) => alert(e?.response?.data?.detail || e.message || "Could not restore invoice."));
      return prev.slice(0, -1);
    });
  };
  const redo = () => {
    setRedoStack((prev) => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      const src = isNonPo(last.row) ? "non_po" : "po";
      setDeletedIds((ids) => { const next = new Set(ids); next.add(rowKey(last.row)); return next; });
      setUndoStack((u) => [...u, last]);
      pushLog("deleted", last.row);
      api.post(`/email-agent/invoices/${last.row.id}/delete?source=${src}`).then(() => setRows((rws) => rws.filter((r) => rowKey(r) !== rowKey(last.row)))).catch((e) => alert(e?.response?.data?.detail || e.message || "Could not delete invoice."));
      return prev.slice(0, -1);
    });
  };
  const restoreFromTab = async (row) => {
    const src = isNonPo(row) ? "non_po" : "po";
    try {
      await api.post(`/email-agent/invoices/${row.id}/restore?source=${src}`);
      setDeletedRows((prev) => prev.filter((r) => rowKey(r) !== rowKey(row)));
      setDeletedIds((ids) => { const n = new Set(ids); n.delete(rowKey(row)); return n; });
      pushLog("restored", row);
      showToast(`Restored ${row.invoice_number || `#${row.id}`}`);
      await load();
    } catch (e) { alert(e?.response?.data?.detail || e.message || "Could not restore invoice."); }
  };
  const clearLog = () => { setLog([]); };

  const saveEdits = async (row, changes) => {
    try {
      try { await api.patch(`/email-agent/invoices/${row.id}`, changes); }
      catch { await api.post(`/email-agent/invoices/${row.id}/edit`, changes); }
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, ...changes } : r)));
      setSelected((s) => (s && s.id === row.id ? { ...s, ...changes } : s));
    } catch (e) { alert(e?.response?.data?.detail || e.message || "Could not save changes."); throw e; }
  };

  const saveLine = async (row, lineId, changes) => {
    try {
      const { data } = await api.patch(`/email-agent/invoices/${row.id}/lines/${lineId}`, changes);
      const applyLine = (r) => {
        if (r.id !== row.id) return r;
        const updated = (r.invoice_lines || []).map((l) => (l.id === lineId ? { ...l, ...(data?.line || changes) } : l));
        return { ...r, invoice_lines: updated };
      };
      setRows((prev) => prev.map(applyLine));
      setSelected((s) => (s && s.id === row.id ? applyLine(s) : s));
    } catch (e) { alert(e?.response?.data?.detail || e.message || "Could not save GL for this line."); throw e; }
  };

  const sendValidationEmail = async (row, payload) => {
    const src = isNonPo(row) ? "non_po" : "po";
    try {
      await api.post(`/email-agent/invoices/${row.id}/send-validation-email?source=${src}`, payload);
      const stamp = new Date().toISOString();
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, validation_email_sent_at: stamp } : r)));
      setSelected((s) => (s && s.id === row.id ? { ...s, validation_email_sent_at: stamp } : s));
      showToast(`Email sent to ${payload.to_email}`);
    } catch (e) { alert(e?.response?.data?.detail || e.message || "Could not send the email."); throw e; }
  };

  const toggleSort = (key) => { if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc")); else { setSortKey(key); setSortDir(key === "supplier" ? "asc" : "desc"); } };
  const typeOptions = [{ value: "all", label: "All" }, { value: "2way", label: "2-way" }, { value: "3way", label: "3-way" }, { value: "nonpo", label: "Non-PO" }];
  const deletedCount = deletedIds.size;

  return (
    <>
      {toast && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[80] flex items-center gap-3 bg-[#4A2C17] text-white px-4 py-2.5 shadow-2xl" data-testid="delete-toast" role="status">
          <Trash size={14} weight="bold" className="shrink-0" /><span className="text-xs font-medium">{toast.msg}</span>
          <button onClick={() => { undo(); setToast(null); }} disabled={undoStack.length === 0} className="ml-1 inline-flex items-center gap-1 border border-white/40 px-2 py-1 text-[10px] font-bold uppercase tracking-wider hover:bg-white/10 disabled:opacity-40"><ArrowUUpLeft size={11} weight="bold" /> Undo</button>
        </div>
      )}

      <PageHeader
        eyebrow="Email Agent · Invoice Automation"
        title="Approvals"
        subtitle="Invoices extracted from your inbox, staged here for review before they post to Oracle Fusion."
        testid="email-agent-title"
        actions={
          <div className="flex items-center gap-2 relative">
            {connEmail && <span className="hidden sm:flex items-center gap-1.5 text-[11px] text-gray-500 border border-gray-200 px-2.5 py-1.5"><EnvelopeSimple size={13} weight="bold" className="text-[#4A2C17]" /><span className="font-mono">{connEmail}</span></span>}
            <div className="relative">
              <button onClick={() => setLogOpen((v) => !v)} data-testid="log-toggle" aria-label="Activity log" className={`relative border px-3 py-2 text-xs font-bold uppercase tracking-wider flex items-center gap-2 ${logOpen ? "border-[#4A2C17] text-[#4A2C17] bg-[#4A2C17]/5" : "border-gray-300 text-gray-600 hover:border-[#4A2C17] hover:text-[#4A2C17]"}`}>
                <ClockCounterClockwise size={13} weight="bold" /> Log
                {deletedCount > 0 && <span className="ml-0.5 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 bg-[#4A2C17] text-white text-[9px] font-bold">{deletedCount}</span>}
              </button>
              <ActivityLog open={logOpen} onClose={() => setLogOpen(false)} entries={log} canUndo={undoStack.length > 0} canRedo={redoStack.length > 0} onUndo={undo} onRedo={redo} onClear={clearLog} />
            </div>
            <button onClick={load} className="border border-[#0020C2] text-[#0020C2] px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-[#0020C2] hover:text-white flex items-center gap-2"><ArrowsClockwise size={13} weight="bold" className={loading ? "animate-spin" : ""} /> Refresh</button>
            <button onClick={disconnect} data-testid="email-disconnect" className="border border-[#DC2626] text-[#DC2626] px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-red-50 flex items-center gap-2"><SignOut size={13} weight="bold" /> Disconnect</button>
          </div>
        }
      />

      <div className="px-8 py-6 space-y-6">
        <ActiveJobs onJobComplete={() => load()} />
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <Stat icon={Clock}       label="Pending"   value={pending.length}  sub="Awaiting review" accent="text-amber-500" />
          <Stat icon={ShieldCheck} label="Approved"  value={approved.length} sub="Ready to post" accent="text-[#0020C2]" />
          <Stat icon={CheckCircle} label="Created"   value={created.length}  sub="Posted to Oracle" accent="text-emerald-500" />
          <Stat icon={WarningCircle} label="Review"  value={failed.length}   sub="Need attention" accent="text-[#DC2626]" />
          <Stat icon={Coins}       label="Pending value" value={money(pendingValue, currency)} sub="Awaiting approval" />
        </div>

        <div className="flex items-center gap-1 border-b border-gray-200">
          <button onClick={() => setTab("invoices")} data-testid="tab-invoices" className={`px-4 py-2 text-xs font-bold uppercase tracking-wider border-b-2 -mb-px transition-colors ${tab === "invoices" ? "border-[#0020C2] text-[#0020C2]" : "border-transparent text-gray-400 hover:text-gray-600"}`}>For approval · {pending.length + failed.length}</button>
          <button onClick={() => setTab("approved")} data-testid="tab-approved" className={`px-4 py-2 text-xs font-bold uppercase tracking-wider border-b-2 -mb-px transition-colors ${tab === "approved" ? "border-[#0020C2] text-[#0020C2]" : "border-transparent text-gray-400 hover:text-gray-600"}`}>Approved · {approved.length}</button>
          <button onClick={() => setTab("created")} data-testid="tab-created" className={`px-4 py-2 text-xs font-bold uppercase tracking-wider border-b-2 -mb-px transition-colors ${tab === "created" ? "border-[#0020C2] text-[#0020C2]" : "border-transparent text-gray-400 hover:text-gray-600"}`}>Created · {created.length}</button>
          <button onClick={() => setTab("deleted")} data-testid="tab-deleted" className={`px-4 py-2 text-xs font-bold uppercase tracking-wider border-b-2 -mb-px transition-colors flex items-center gap-1.5 ${tab === "deleted" ? "border-[#4A2C17] text-[#4A2C17]" : "border-transparent text-gray-400 hover:text-gray-600"}`}><Trash size={12} weight="bold" /> Deleted{deletedRows.length ? ` · ${deletedRows.length}` : ""}</button>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 border border-gray-300 bg-white px-3 py-2 flex-1 min-w-[220px] max-w-md">
            <MagnifyingGlass size={15} weight="bold" className="text-gray-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search supplier, invoice #, or PO…" data-testid="email-agent-search" className="flex-1 outline-none text-sm bg-transparent" />
            {q && <button onClick={() => setQ("")}><X size={13} weight="bold" className="text-gray-400" /></button>}
          </div>
          <div className="flex items-center gap-1.5"><Funnel size={13} weight="bold" className="text-gray-400" /><Segmented options={typeOptions} value={typeFilter} onChange={setType} /></div>
          <div className="flex items-center gap-1">
            {[{ k: "date", l: "Date" }, { k: "amount", l: "Amount" }, { k: "supplier", l: "Supplier" }].map((s) => (
              <button key={s.k} onClick={() => toggleSort(s.k)} className={`inline-flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-wider border ${sortKey === s.k ? "border-[#0020C2] text-[#0020C2] bg-blue-50" : "border-gray-200 text-gray-500 hover:text-gray-800"}`}>
                {s.l}{sortKey === s.k && (sortDir === "asc" ? <ArrowUp size={11} weight="bold" /> : <ArrowDown size={11} weight="bold" />)}
              </button>
            ))}
          </div>
        </div>

        {err && <div className="border border-[#DC2626] bg-red-50 px-4 py-3 text-xs text-[#DC2626] flex items-center gap-2"><WarningCircle size={14} weight="fill" /> {err}</div>}

        {loading ? (
          <div className="py-16 text-center text-xs tracking-[0.2em] uppercase font-bold text-gray-500">Loading…</div>
        ) : tab === "invoices" ? (
          <>
            <Group icon={ShieldCheck} title="For approval" count={pending.length} accent>
              {pending.length === 0 ? <Empty>No invoices waiting for review.</Empty> : pending.map((r) => <InvoiceRow key={rowKey(r)} row={r} onOpen={setSelected} />)}
            </Group>
            <Group icon={WarningCircle} title="Needs review" count={failed.length}>
              {failed.length === 0 ? <Empty>Nothing needs attention here.</Empty> : failed.map((r) => <InvoiceRow key={rowKey(r)} row={r} onOpen={setSelected} />)}
            </Group>
          </>
        ) : tab === "approved" ? (
          <Group icon={ShieldCheck} title="Approved · ready to post to Oracle" count={approved.length} accent>
            {approved.length === 0 ? <Empty>Approve an invoice and it will appear here, ready to post to Oracle.</Empty> : approved.map((r) => <InvoiceRow key={rowKey(r)} row={r} onOpen={setSelected} />)}
          </Group>
        ) : tab === "created" ? (
          <Group icon={CheckCircle} title="Created in Oracle" count={created.length}>
            {created.length === 0 ? <Empty>Posted invoices will appear here once created in Oracle.</Empty> : created.map((r) => <InvoiceRow key={rowKey(r)} row={r} onOpen={setSelected} />)}
          </Group>
        ) : (
          <Group icon={Trash} title="Deleted · hidden from the lists" count={deletedRows.length}>
            {deletedLoading ? <Empty>Loading deleted invoices…</Empty> : deletedRows.length === 0 ? <Empty>No deleted invoices. Anything you delete lands here and can be restored.</Empty> : deletedRows.map((r) => <DeletedRow key={rowKey(r)} row={r} onRestore={restoreFromTab} onOpen={setSelected} />)}
          </Group>
        )}
      </div>

      <DetailDrawer row={selected} onClose={() => setSelected(null)} onApprove={approve} onPost={postToOracle} onReject={reject} onDelete={deleteInvoice} onSaveEdits={saveEdits} onSaveLine={saveLine} onSendValidation={sendValidationEmail} busy={actionBusy} />
    </>
  );
}

/* -- Page shell ----------------------------------------------------------- */
export default function EmailAgentPage() {
  const [connected, setConnected] = React.useState(false);
  return connected ? <Dashboard onDisconnect={() => setConnected(false)} /> : <ConnectGate onConnected={() => setConnected(true)} />;
}